import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Share2, ShieldCheck, Coins, Users, Sparkles, ArrowRight } from "lucide-react";
import TopBar from "@/components/app/TopBar";
import Sidebar from "@/components/app/Sidebar";
import { fmtMoney, fmtNumber } from "@/lib/format";
import { currentUser } from "@/lib/mockData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const USER_ID = currentUser.id;

export default function Referrals() {
  const [stats, setStats] = useState(null);
  const [list, setList] = useState([]);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const load = async () => {
    try {
      await axios.post(`${API}/referrals/code?user_id=${USER_ID}`);
      const [s, l] = await Promise.all([
        axios.get(`${API}/referrals/stats/${USER_ID}`),
        axios.get(`${API}/referrals/${USER_ID}/list`),
      ]);
      setStats(s.data);
      setList(l.data);
    } catch {
      toast.error("No se pudo cargar el panel de referidos");
    }
  };

  useEffect(() => { load(); }, []);

  const code = stats?.code;
  const shareUrl = code ? `https://ovamx.com/?ref=${code}` : "";

  const copy = async (text, label = "Copiado") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const redeem = async (e) => {
    e.preventDefault();
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const fakeInviteeId = `u-demo-${Math.random().toString(36).slice(2, 8)}`;
      await axios.post(`${API}/referrals/redeem`, {
        code: redeemCode.trim().toUpperCase(),
        invitee_id: fakeInviteeId,
        invitee_name: "Demo invitado",
      });
      toast.success("Código aplicado. ¡Comienza a generar para tu padrino!");
      setRedeemCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código no válido");
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div data-testid="referrals-page" className="bg-cream min-h-screen text-ink">
      <TopBar />
      <div className="max-w-[1600px] mx-auto flex">
        <Sidebar />

        <main className="flex-1 min-w-0 px-4 md:px-6 lg:px-8 py-5">
          <div className="max-w-[1100px] mx-auto">
            {/* Hero */}
            <section className="rounded-2xl border hairline bg-ink text-cream p-6 md:p-10 mb-6 relative overflow-hidden">
              <div className="grain absolute inset-0 opacity-25 pointer-events-none" />
              <div className="relative grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">
                    ◊ El Golpe de Gracia
                  </p>
                  <h1 className="mt-3 font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95]">
                    Cuando ellos ganan, <span className="italic text-coral">tú también</span>.
                  </h1>
                  <p className="mt-5 max-w-xl text-base md:text-lg text-cream/85 leading-relaxed">
                    Invita amigos a Ovamx. Cuando moneticen — música, contenido, marketplace, propinas — tú recibes un porcentaje real, de por vida. Una red que crece contigo y paga contigo.
                  </p>

                  {/* Code box */}
                  <div className="mt-7 inline-flex items-stretch gap-2 bg-cream/[0.06] border border-cream/15 rounded-2xl p-2">
                    <div className="px-5 py-3 flex items-center gap-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/55">tu código</span>
                      <span data-testid="referral-code" className="font-serif text-2xl tracking-tighter">{code || "—"}</span>
                    </div>
                    <button
                      data-testid="copy-code"
                      disabled={!code}
                      onClick={() => copy(code, "Código copiado")}
                      className="inline-flex items-center gap-2 bg-cream/10 hover:bg-coral hover:text-cream rounded-xl px-4 text-xs transition disabled:opacity-50"
                    >
                      <Copy className="w-3.5 h-3.5" /> Copiar
                    </button>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      data-testid="copy-link"
                      disabled={!code}
                      onClick={() => copy(shareUrl, "Enlace copiado")}
                      className="inline-flex items-center gap-2 bg-coral hover:bg-coral-hover text-cream rounded-full px-5 py-2.5 text-xs transition disabled:opacity-50"
                    >
                      <Share2 className="w-3.5 h-3.5" /> Copiar enlace de invitación
                    </button>
                    <span data-testid="referral-share-url" className="font-mono text-[11px] text-cream/55 truncate max-w-[260px]">{shareUrl || "—"}</span>
                  </div>
                </div>

                {/* Tier visualization */}
                <div className="col-span-12 md:col-span-5 grid grid-cols-1 gap-3">
                  <TierCard tier={1} pct={stats?.tier_1_pct ?? 10} title="Amigos directos" desc="De cada $1 que ganen tus invitados directos." />
                  <TierCard tier={2} pct={stats?.tier_2_pct ?? 3} title="Red de tu red" desc="De cada $1 que ganen los invitados de tus invitados." />
                  <div className="rounded-2xl border border-cream/15 bg-cream/[0.04] p-4">
                    <div className="flex items-center gap-2 mb-1.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-coral" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/55">Garantía Ovamx</span>
                    </div>
                    <p className="text-[13px] text-cream/85 leading-relaxed">
                      Comisión transparente y de por vida. Sin letra pequeña. Pago automático en {`<`} 24 h.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Stats */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <KPI testid="kpi-invitees"  icon={Users}     k={fmtNumber(stats?.total_invitees ?? 0)}    v="invitados totales" />
              <KPI testid="kpi-active"    icon={Sparkles}  k={fmtNumber(stats?.earning_invitees ?? 0)}  v="generando ingresos" />
              <KPI testid="kpi-network"   icon={ArrowRight} k={fmtMoney(stats?.invitee_earnings_total_usd ?? 0)} v="generado por tu red" />
              <KPI testid="kpi-earnings"  icon={Coins}     k={fmtMoney(stats?.total_earnings_usd ?? 0)}  v="tus ganancias" highlight />
            </section>

            {/* How it works */}
            <section className="border hairline bg-white rounded-2xl p-6 md:p-8 mb-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral mb-3">cómo funciona</p>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight mb-6">Tres pasos. Cero letra pequeña.</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 rounded-xl overflow-hidden border hairline">
                {[
                  { n: "01", t: "Compartes tu código",   d: "Tu enlace personal te identifica de por vida con cada amigo invitado." },
                  { n: "02", t: "Ellos crean en Ovamx",  d: "Música, contenido, ventas, marketplace, propinas — cualquier flujo de monetización cuenta." },
                  { n: "03", t: "Tú cobras automático",  d: "10% directo + 3% de la red secundaria. Pago en menos de 24 h, sin tener que reclamarlo." },
                ].map((s) => (
                  <div key={s.n} className="bg-white p-6">
                    <span className="font-mono text-[10px] text-coral">{s.n}</span>
                    <h4 className="font-serif text-2xl mt-3 leading-tight">{s.t}</h4>
                    <p className="text-sm text-ink/75 mt-2 leading-relaxed">{s.d}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Redeem someone else's code */}
            <section className="border hairline bg-white rounded-2xl p-6 md:p-8 mb-6">
              <div className="flex flex-wrap items-end gap-4 justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-coral mb-2">canjear código</p>
                  <h3 className="font-serif text-3xl tracking-tight">¿Te invitaron? Aplica el código aquí.</h3>
                  <p className="text-sm text-ink-mid mt-1">Solo se puede aplicar una vez por cuenta. Tu padrino lo nota al instante.</p>
                </div>
                <form onSubmit={redeem} className="flex gap-2">
                  <input
                    data-testid="redeem-input"
                    value={redeemCode}
                    onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                    placeholder="OVX-XXXX-XXXX"
                    className="bg-cream border hairline rounded-full px-5 py-2.5 text-sm w-56 outline-none focus:border-coral font-mono"
                  />
                  <button data-testid="redeem-submit" disabled={redeeming} className="bg-ink text-cream rounded-full px-5 py-2.5 text-sm hover:bg-coral transition disabled:opacity-50">
                    Aplicar
                  </button>
                </form>
              </div>
            </section>

            {/* Invitee list */}
            <section className="border hairline bg-white rounded-2xl p-3 md:p-5">
              <div className="flex items-center justify-between mb-3 px-2">
                <h3 className="font-serif text-2xl tracking-tight">Tu red</h3>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
                  {list.length} miembro{list.length === 1 ? "" : "s"}
                </span>
              </div>
              {list.length === 0 ? (
                <div data-testid="referral-empty" className="px-6 py-12 text-center">
                  <p className="text-base text-ink-mid">Tu red está esperando. Comparte tu código y comienza el efecto en cascada.</p>
                </div>
              ) : (
                <div className="grid grid-cols-12 gap-3 px-3 pb-2 border-b hairline font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
                  <div className="col-span-6">invitado</div>
                  <div className="col-span-3 text-right">han generado</div>
                  <div className="col-span-3 text-right">tu cuota</div>
                </div>
              )}
              {list.map((r) => (
                <div key={r.id} data-testid={`referral-item-${r.id}`} className="grid grid-cols-12 gap-3 px-3 py-3 border-b hairline last:border-b-0 items-center">
                  <div className="col-span-6 text-sm">{r.invitee_name || r.invitee_id}</div>
                  <div className="col-span-3 text-right text-sm font-mono">{fmtMoney(r.invitee_earnings_total)}</div>
                  <div className="col-span-3 text-right text-sm text-coral font-mono">{fmtMoney(r.earnings_to_date)}</div>
                </div>
              ))}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, k, v, highlight, testid }) {
  return (
    <div data-testid={testid} className={`border hairline rounded-2xl p-4 ${highlight ? "bg-coral text-cream border-coral" : "bg-white"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${highlight ? "text-cream" : "text-coral"}`} strokeWidth={1.7} />
        <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${highlight ? "text-cream/85" : "text-ink-mid"}`}>{v}</p>
      </div>
      <p className="font-serif text-3xl tracking-tighter">{k}</p>
    </div>
  );
}

function TierCard({ tier, pct, title, desc }) {
  return (
    <div className="rounded-2xl border border-cream/15 bg-cream/[0.04] p-4 flex items-center gap-4">
      <div className="font-serif text-5xl tracking-tighter text-coral leading-none">{pct}%</div>
      <div className="flex-1">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/55">Nivel {tier}</p>
        <p className="text-sm font-medium text-cream mt-1">{title}</p>
        <p className="text-xs text-cream/70 mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
